<script setup lang="ts">

import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import { computed, ref } from 'vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import { usePlayerColorStore } from '@/stores/playerColorStore.ts'
import MediumPlusModel from '@/components/Interface/Modal/ModelSizes/MediumPlusModel.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import router from '@/router'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const gameStore = useGameStore()
const playerRelationStore = usePlayerRelationStore()
const allianceStore = useAllianceStore()
const chatStore = useChatStore()
const playerColorStore = usePlayerColorStore()
const backend = new Backend();

/**
 * Color picker state
 */
const colorHue = ref(0) // 0-360 for hue value
const showColorPicker = ref(false)

const selectedColor = computed(() => {
  const h = colorHue.value
  const s = 100
  const l = 50

  // HSL to RGB conversion
  const c = (1 - Math.abs(2 * l / 100 - 1)) * s / 100
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l / 100 - c / 2

  let r = 0, g = 0, b = 0

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x
  }

  const rHex = Math.round((r + m) * 255).toString(16).padStart(2, '0')
  const gHex = Math.round((g + m) * 255).toString(16).padStart(2, '0')
  const bHex = Math.round((b + m) * 255).toString(16).padStart(2, '0')

  return `#${rHex}${gHex}${bHex}`
})

/**
 * Convert hex color to hue value (0-360)
 */
const hexToHue = (hex: string): number => {
  // Remove # if present
  hex = hex.replace('#', '')

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  if (delta === 0) {
    return 0
  }

  let hue = 0
  if (max === r) {
    hue = ((g - b) / delta) % 6
  } else if (max === g) {
    hue = (b - r) / delta + 2
  } else {
    hue = (r - g) / delta + 4
  }

  hue = Math.round(hue * 60)
  if (hue < 0) {
    hue += 360
  }

  return hue
}

const toggleColorPicker = () => {
  // When opening the picker, initialize slider to current color if one exists
  if (!showColorPicker.value && currentPlayerColor.value) {
    colorHue.value = hexToHue(currentPlayerColor.value)
  }
  showColorPicker.value = !showColorPicker.value
}

/**
 * Functions
 */
const declareWar = () => {

  let warMessage = t('diplomacy.audience.confirmWar')

  if (playerAlliance.value) {
    warMessage += `<br><br><span class="text-red-500 font-semibold">${t('diplomacy.audience.warAllianceWarning', { name: relation.value?.name, alliance: playerAlliance.value.name })}</span>`
  }

  gameStore.modal2Type = 'confirmationDelete';
  gameStore.modal2Data = {
    title: t('common.confirmation.areYouSure'),
    message: warMessage,
    actionText: t('common.actions.yes'),
    cancelText: t('common.actions.no'),
    action: () => {
      backend.relationDeclareWar(gameStore.modalData.relation.playerId).then(() => {

      })
    },
  };

}

const requestOrAccept = (type: string) => {
  backend.relationRequest(gameStore.modalData.relation.playerId, type).then(() => {

  })
}

const rejectRequest = (type: string) => {
  backend.relationReject(gameStore.modalData.relation.playerId, type).then(() => {

  })
}

const stopNapRenewal = () => {

  gameStore.modal2Type = 'confirmationDelete';
  gameStore.modal2Data = {
    title: t('common.confirmation.areYouSure'),
    message: t('diplomacy.audience.confirmNapStop'),
    actionText: t('common.actions.yes'),
    cancelText: t('common.actions.no'),
    action: () => {
      backend.stopNAPRenewal(gameStore.modalData.relation.playerId).then(() => {
      })
    },
  };
}

const openChat = async () => {
  try {
    const roomId = await chatStore.openOrCreateChatWithPlayer(gameStore.modalData.relation.playerId)
    gameStore.modalType = ''
    router.push(`/communication/${roomId}`)
  } catch (error) {
    logger.error('Error opening chat:', error)
  }
}

/**
 * Computed
 */
const relation = computed(() => {
  return playerRelationStore.findRelationByPlayerId(gameStore.modalData.relation.playerId)
})

// Get player's alliance data if they're in an alliance
const playerAlliance = computed(() => {
  if (relation.value) {
    // We need to search all alliances to find the one this player belongs to
    for (const alliance of allianceStore.alliances.values()) {
      // Only include non-deleted alliances that contain the player
      if (!alliance.deleted && alliance.members.includes(relation.value.playerId)) {
        return alliance
      }
    }
  }
  return null
})

const canAttackPlayer = computed(() => {
  if (relation.value) {
    return gameStore.canBeAttacked(relation.value.playerId)
  }
  return false
})

const isMuted = computed(() => {
  return chatStore.isPlayerMuted(gameStore.modalData.relation.playerId)
})

const currentPlayerColor = computed(() => {
  return playerColorStore.getColorForPlayer(gameStore.modalData.relation.playerId)
})

const setPlayerColor = async () => {
  try {
    await backend.setPlayerColorLabel(gameStore.modalData.relation.playerId, selectedColor.value)
    showColorPicker.value = false
  } catch (error) {
    logger.error('Error setting player color:', error)
  }
}

const clearPlayerColor = async () => {
  try {
    await backend.deletePlayerColorLabel(gameStore.modalData.relation.playerId)
  } catch (error) {
    logger.error('Error clearing player color:', error)
  }
}

</script>

<template>

  <MediumPlusModel :title="relation.name + (playerAlliance ? ' (' + playerAlliance.name + ')' : '')">
    <div class="p-3 md:p-3">
      <!-- Mobile: Image and Chat button at top -->
      <div class="sm:hidden mb-4">
        <div class="flex items-start gap-3">
          <div class="relative shrink-0">
            <img :src="relation.profileImage" class="rounded w-24 h-24 object-cover" />
            <div v-if="playerAlliance && playerAlliance.allianceImage"
                 class="absolute top-1 right-1 rounded pointer-events-none">
              <img :src="playerAlliance.allianceImage"
                   class="rounded w-8 h-8 border border-slate-800 z-10 pointer-events-auto" :title="t('diplomacy.alliance.title')" />
            </div>
          </div>
          <div class="flex-grow flex flex-col gap-2">
            <ActionButton @click="openChat" class="w-full">
              {{ t('diplomacy.player.chatWith', { name: relation.name }) }}
            </ActionButton>
            <DeleteButton v-if="isMuted" @click="chatStore.unmutePlayer(gameStore.modalData.relation.playerId)" class="w-full">
              {{ t('diplomacy.player.unmute', { name: relation.name }) }}
            </DeleteButton>
            <DeleteButton v-else color="orange" @click="chatStore.mutePlayer(gameStore.modalData.relation.playerId)" class="w-full">
              {{ t('diplomacy.player.mute', { name: relation.name }) }}
            </DeleteButton>
            <!-- Color picker for map territory -->
            <StandardCard :title="t('diplomacy.player.mapTerritoryColor')" top-padding="2">
              <!-- Color is set: show color display with trash icon -->
              <div v-if="currentPlayerColor && !showColorPicker" class="flex gap-2 items-center">
                <div
                  @click="toggleColorPicker"
                  class="flex gap-2 items-center flex-grow cursor-pointer hover:bg-slate-700 p-1 rounded transition-colors"
                >
                  <div class="w-6 h-6 rounded border border-slate-600 shrink-0" :style="{ backgroundColor: currentPlayerColor }"></div>
                  <div class="text-xs text-slate-300">
                    {{ currentPlayerColor }}
                  </div>
                </div>
                <DeleteButton @click="clearPlayerColor" :for-icon="true">
                  <GenericIcon type="trash" />
                </DeleteButton>
              </div>
              <!-- No color set and picker closed: show only "Set Color" button -->
              <div v-else-if="!currentPlayerColor && !showColorPicker">
                <ActionButton @click="showColorPicker = true" class="w-full text-xs py-1">
                  {{ t('diplomacy.player.setColor') }}
                </ActionButton>
              </div>
              <!-- Picker is open: show slider and buttons -->
              <div v-else class="flex flex-col gap-2">
                <div class="flex gap-2 items-center">
                  <div class="w-6 h-6 rounded border border-slate-600 shrink-0" :style="{ backgroundColor: selectedColor }"></div>
                  <input type="range" v-model.number="colorHue" min="0" max="360" class="flex-grow" />
                </div>
                <div class="flex gap-2">
                  <ActionButton @click="setPlayerColor" class="flex-grow text-xs py-1">
                    {{ t('diplomacy.player.setColor') }}
                  </ActionButton>
                  <DeleteButton v-if="currentPlayerColor" @click="clearPlayerColor" :for-icon="true">
                    <GenericIcon type="trash" />
                  </DeleteButton>
                </div>
              </div>
            </StandardCard>
          </div>
        </div>
      </div>

      <div class="flex">
        <!-- Desktop: Image and Chat button on the left -->
        <div class="hidden sm:block relative">
          <img :src="relation.profileImage" class="rounded" style="width: 40rem;" />
          <div v-if="playerAlliance && playerAlliance.allianceImage"
               class="absolute inset-0 rounded pointer-events-none">
            <img :src="playerAlliance.allianceImage"
                 class="absolute top-2 right-2 rounded w-14 h-14 border border-slate-800 z-10 pointer-events-auto" :title="t('diplomacy.alliance.title')" />
          </div>
          <div class="mt-3 flex flex-col gap-2">
            <ActionButton @click="openChat" class="w-full">
              {{ t('diplomacy.player.chatWith', { name: relation.name }) }}
            </ActionButton>
            <DeleteButton v-if="isMuted" @click="chatStore.unmutePlayer(gameStore.modalData.relation.playerId)" class="w-full">
              {{ t('diplomacy.player.unmute', { name: relation.name }) }}
            </DeleteButton>
            <DeleteButton v-else color="orange" @click="chatStore.mutePlayer(gameStore.modalData.relation.playerId)" class="w-full">
              {{ t('diplomacy.player.mute', { name: relation.name }) }}
            </DeleteButton>
            <!-- Color picker for map territory -->
            <StandardCard :title="t('diplomacy.player.mapTerritoryColor')" top-padding="0">
              <!-- Color is set: show color display with trash icon -->
              <div v-if="currentPlayerColor && !showColorPicker" class="flex gap-2 items-center">
                <div
                  @click="toggleColorPicker"
                  class="flex gap-2 items-center flex-grow cursor-pointer hover:bg-slate-700 p-1 rounded transition-colors"
                >
                  <div class="w-6 h-6 rounded border border-slate-600 shrink-0" :style="{ backgroundColor: currentPlayerColor }"></div>
                  <div class="text-xs text-slate-300">
                    {{ currentPlayerColor }}
                  </div>
                </div>
                <DeleteButton @click="clearPlayerColor" :for-icon="true">
                  <GenericIcon type="trash" />
                </DeleteButton>
              </div>
              <!-- No color set and picker closed: show only "Set Color" button -->
              <div v-else-if="!currentPlayerColor && !showColorPicker">
                <ActionButton @click="showColorPicker = true" class="w-full text-xs py-1">
                  {{ t('diplomacy.player.setColor') }}
                </ActionButton>
              </div>
              <!-- Picker is open: show slider and buttons -->
              <div v-else class="flex flex-col gap-2">
                <div class="flex gap-2 items-center">
                  <div class="w-6 h-6 rounded border border-slate-600 shrink-0" :style="{ backgroundColor: selectedColor }"></div>
                  <input type="range" v-model.number="colorHue" min="0" max="360" class="flex-grow" />
                </div>
                <div class="flex gap-2">
                  <ActionButton @click="setPlayerColor" class="flex-grow text-xs py-1">
                    {{ t('diplomacy.player.setColor') }}
                  </ActionButton>
                  <DeleteButton v-if="currentPlayerColor" @click="clearPlayerColor" :for-icon="true">
                    <GenericIcon type="trash" />
                  </DeleteButton>
                </div>
              </div>
            </StandardCard>
          </div>
        </div>
        <div class="flex-grow">
          <div class="sm:ms-6">
            <ol class="relative border-s border-slate-700 text-slate-300">
              <!-- Enemy -->
              <li class="ms-6">
                <span :class="
                {
                  'absolute flex items-center justify-center w-4 h-4 rounded -start-2 ring-4 ring-slate-900': true,
                  'bg-slate-800': relation.relation !== 'enemy',
                  'bg-red-800': relation.relation === 'enemy'
                }">
                </span>
                <h3 class="font-medium leading-tight text-slate-300">{{ t('diplomacy.audience.states.enemy.title') }}</h3>
                <div class="rounded p-1 mt-2 mb-2 text-subtext">
                  {{ t('diplomacy.audience.states.enemy.description') }}
                </div>
                <div v-if="['enemy'].includes(relation.relation)" class="pb-2">
                  <div v-if="relation.iRequested === 'peace'" class="p-2 bg-slate-800 rounded">
                    {{ t('diplomacy.audience.states.enemy.peaceRequestSent') }}
                  </div>
                  <div v-else-if="relation.otherRequested === 'peace'">
                    <div class="p-2 bg-slate-800 rounded">
                      <div class="pb-2">{{ t('diplomacy.audience.states.enemy.peaceOffered', { name: relation.name }) }}</div>
                      <ActionButton @click.once="requestOrAccept('peace')">{{ t('common.actions.accept') }}</ActionButton>
                      <DeleteButton color="red" @click.once="rejectRequest('peace')">{{ t('diplomacy.relations.reject') }}</DeleteButton>
                    </div>
                  </div>
                  <div v-else>
                    <DeleteButton @click.once="requestOrAccept('peace')">{{ t('diplomacy.relations.requestPeace') }}</DeleteButton>
                  </div>
                </div>
              </li>
              <!-- Neutral -->
              <li class="ms-6">
                <span :class="
                {
                  'absolute flex items-center justify-center w-4 h-4 rounded-full -start-2 ring-4 ring-slate-900': true,
                  'bg-slate-800': relation.relation !== 'neutral',
                  'bg-yellow-400': relation.relation === 'neutral'
                }">
                </span>
                <h3 class="font-medium leading-tight text-slate-300">{{ t('diplomacy.audience.states.neutral.title') }}</h3>
                <div class="rounded p-1 mt-2 mb-2 text-subtext">
                  {{ t('diplomacy.audience.states.neutral.description') }}
                </div>
                <div v-if="['unknown', 'neutral'].includes(relation.relation)" class="pb-2">
                  <div v-if="relation.iRequested === 'friend'" class="p-2 bg-slate-800 rounded">
                    {{ t('diplomacy.audience.states.neutral.friendshipRequestSent') }}
                  </div>
                  <div v-else-if="relation.otherRequested === 'friend'">
                    <div class="p-2 bg-slate-800 rounded">
                      <div class="pb-2">{{ t('diplomacy.audience.states.neutral.friendshipOffered', { name: relation.name }) }}</div>
                      <ActionButton @click.once="requestOrAccept('friend')">{{ t('common.actions.accept') }}</ActionButton>
                      <DeleteButton color="red" @click.once="rejectRequest('friend')">{{ t('diplomacy.relations.reject') }}</DeleteButton>
                    </div>
                  </div>
                  <div v-else>
                    <ActionButton @click.once="requestOrAccept('friend')">{{ t('diplomacy.relations.sendFriendshipRequest') }}</ActionButton>
                  </div>
                  <div v-if="['friend', 'unknown', 'neutral'].includes(relation.relation) && (canAttackPlayer || gameStore.combatMode === 'FFA')" class="pt-2">
                    <DeleteButton color="red" @click.once="declareWar">{{ t('diplomacy.relations.declareWar') }}</DeleteButton>
                  </div>
                  <div v-else-if="['friend', 'unknown', 'neutral'].includes(relation.relation) && !canAttackPlayer && gameStore.combatMode !== 'FFA'" class="pt-2">
                    <div class="p-2 bg-slate-800 rounded text-slate-400 text-sm">
                      <span class="text-yellow-400">⚠️</span> {{ t('diplomacy.player.protected') }}
                    </div>
                  </div>
                </div>
              </li>
              <!-- Friend -->
              <li class="ms-6">
                <span :class="
                {
                  'absolute flex items-center justify-center w-4 h-4 rounded-full -start-2 ring-4 ring-slate-900': true,
                  'bg-slate-800': relation.relation !== 'friend',
                  'bg-purple-800': relation.relation === 'friend'
                }">
                </span>
                <h3 class="font-medium leading-tight text-slate-300">{{ t('diplomacy.audience.states.friend.title') }}</h3>
                <div class="rounded p-1 mt-2 mb-2  text-subtext">
                  {{ t('diplomacy.audience.states.friend.description') }}
                </div>
                <div v-if="['friend'].includes(relation.relation)" class="pb-2 rounded">
                  <div v-if="relation.iRequested === 'nap'" class="p-2 bg-slate-800 rounded">
                    {{ t('diplomacy.audience.states.friend.napRequestSent') }}
                  </div>
                  <div v-else-if="relation.otherRequested === 'nap'">
                    <div class="p-2 bg-slate-800 rounded">
                      <div class="pb-2">{{ t('diplomacy.audience.states.friend.napOffered', { name: relation.name }) }}</div>
                      <div class="flex justify-evenly">
                        <ActionButton @click.once="requestOrAccept('nap')">{{ t('common.actions.accept') }}</ActionButton>
                        <DeleteButton color="red" @click.once="rejectRequest('nap')">{{ t('diplomacy.relations.reject') }}</DeleteButton>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <ActionButton @click.once="requestOrAccept('nap')">{{ t('diplomacy.relations.requestNap') }}</ActionButton>
                  </div>
                  <div v-if="['friend', 'unknown', 'neutral'].includes(relation.relation) && (canAttackPlayer || gameStore.combatMode === 'FFA')" class="pt-2">
                    <DeleteButton color="red" @click="declareWar">{{ t('diplomacy.relations.declareWar') }}</DeleteButton>
                  </div>
                  <div v-else-if="['friend', 'unknown', 'neutral'].includes(relation.relation) && !canAttackPlayer && gameStore.combatMode !== 'FFA'" class="pt-2">
                    <div class="p-2 bg-slate-800 rounded text-slate-400 text-sm">
                      <span class="text-yellow-400">⚠️</span> {{ t('diplomacy.player.protected') }}
                    </div>
                  </div>
                </div>
              </li>
              <!-- Ally -->
              <li class="ms-6">
                <span :class="
                {
                  'absolute flex items-center justify-center w-4 h-4 rounded-full -start-2 ring-4 ring-slate-900': true,
                  'bg-slate-800': relation.relation !== 'ally',
                  'bg-cyan-800': relation.relation === 'ally'
                }">
                </span>
                <h3 class="font-medium leading-tight text-slate-300">{{ t('diplomacy.audience.states.ally.title') }}</h3>
                <div class="rounded p-1 mt-2 mb-2 text-subtext ">
                  {{ t('diplomacy.audience.states.ally.description') }}
                </div>
                <div v-if="['ally'].includes(relation.relation)" class="p-2 mb-2 bg-slate-800 rounded">
                  <div v-if="relation.stopNap === 'stop'"><span v-html="t('diplomacy.audience.states.ally.napExpiring', { days: relation.napExpireTurn - gameStore.turn }).replace(t('diplomacy.audience.states.ally.napExpiring', { days: relation.napExpireTurn - gameStore.turn }).match(/expire/i)?.[0] || 'expire', '<span class=\'text-red-500\'>expire</span>')"></span></div>
                  <div v-else>
                    <div class="pb-2"><span v-html="t('diplomacy.audience.states.ally.napAutoRenew', { days: relation.napExpireTurn - gameStore.turn }).replace(t('diplomacy.audience.states.ally.napAutoRenew', { days: relation.napExpireTurn - gameStore.turn }).match(/auto renew/i)?.[0] || 'auto renew', '<span class=\'text-green-500\'>auto renew</span>')"></span></div>
                    <DeleteButton color="red" @click="stopNapRenewal">{{ t('diplomacy.relations.discontinueNap') }}</DeleteButton>
                  </div>
                </div>
              </li>
              <!-- Alliance -->
              <li class="ms-6">
                <span :class="
                {
                  'absolute flex items-center justify-center w-4 h-4 rounded-full -start-2 ring-4 ring-slate-900': true,
                  'bg-slate-800': relation.relation !== 'alliance',
                  'bg-blue-800': relation.relation === 'alliance'
                }">
                </span>
                <h3 class="font-medium leading-tight text-slate-300">{{ t('diplomacy.audience.states.alliance.title') }}</h3>
                <div class="rounded p-1 mt-2 mb-2 text-subtext">
                  {{ t('diplomacy.audience.states.alliance.description') }}
                </div>
              </li>

            </ol>
          </div>
        </div>
      </div>
    </div>
  </MediumPlusModel>

</template>

<style scoped lang="scss">

</style>